import { ReactNode, createContext, useContext, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

// @type
import { CalendarContextProps, CalendarOperationType, CalendarSaveOptions } from "../@types/calendar";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import { CalendarEventDialog } from "components/__haim/Common/CalendarEventDialog";
import useFirestore from "hooks/useFirestore";
import useOrgDirectory from "hooks/useOrgDirectory";
import { useSnackbar } from "notistack5";
import { GenericConverter } from "utils/firestoreConverters";
import {
  ASSIGNED_TASKS_LOOKBACK_DAYS,
  normalizeUserEmail,
  officeTaskErrorKey,
  taskFieldsOf
} from "utils/calendar/officeTasks";
import {
  CalendarEvent,
  CalendarEventType,
  CalendarSettings,
  REST_schema
} from "./../@types/sharedSchema";
import { addHours, roundToNearestMinutes, startOfDay, subDays } from "date-fns";
import useWebotServer from "hooks/useWebotServer";
import { warmCalendarClientNoteStatus } from "hooks/useCalendarClientNoteEnabled";

// ----------------------------------------------------------------------

/** Milliseconds for a value that may be a JS Date or a Firestore Timestamp. */
const toMillis = (value: unknown): number | null => {
  if (!value) return null;
  if (value instanceof Date) return value.getTime();
  // Firestore Timestamp (has toDate) or a raw {seconds} shape.
  const maybeTimestamp = value as { toDate?: () => Date; seconds?: number };
  if (typeof maybeTimestamp.toDate === "function") return maybeTimestamp.toDate().getTime();
  if (typeof maybeTimestamp.seconds === "number") return maybeTimestamp.seconds * 1000;
  const parsed = new Date(value as string | number).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Whether an edit moved the event in time — the only kind of change that should
 * WhatsApp the client. Compares start, end and the all-day flag. When the
 * previous event can't be found (rare edge), returns `true` so a genuine
 * reschedule is never silently dropped.
 */
const didEventTimesChange = (
  previous: CalendarEvent | undefined,
  next: CalendarEvent
): boolean => {
  if (!previous) return true;
  return (
    toMillis(previous.start) !== toMillis(next.start) ||
    toMillis(previous.end) !== toMillis(next.end) ||
    Boolean(previous.allDay) !== Boolean(next.allDay)
  );
};

const CalendarContext = createContext({} as CalendarContextProps);

type CalendarEventEditorState = {
  open: boolean;
  event: CalendarEvent | null;
  onClose: () => void;
};

/**
 * The global event editor's open/close state, read by `CalendarEventEditorHost`.
 *
 * ## Why the dialog is not rendered by the provider itself
 *
 * `CalendarProvider` sits ABOVE `ListingDialogProvider` in `App.tsx`, and must:
 * the listing dialog's events tab reads `useCalendar()`. A dialog rendered here
 * was therefore outside the listing dialog's context, so a listing card inside an
 * event ("פרטים נוספים" in its menu) called the context's no-op default and the
 * click did nothing at all. The state stays here; the host is mounted inside
 * `ListingDialogProvider`, where every context the form's children need exists.
 */
const CalendarEventEditorContext = createContext<CalendarEventEditorState>({
  open: false,
  event: null,
  onClose: () => undefined
});

/** What a LIST ROW needs from the calendar: open the editor on a new task or meeting. */
export type CalendarEditorActions = Pick<CalendarContextProps, "showSingleEventEditorModalByType">;

/**
 * The editor's "open on a new event" action, under a value that NEVER changes — read by
 * `useCalendarEditorActions()`.
 *
 * ## Why it is not just `useCalendar()`
 *
 * `CalendarContext`'s value is a new object on every render of this provider, and the
 * provider renders on every calendar snapshot, every open and close of the editor, and every
 * change of `useFirestore()` / `useOrgDirectory()` above it. Every reader of `useCalendar()`
 * re-renders with it, `React.memo` or not. The listings board mounts a row per listing (200
 * a page, all of them, no virtualisation) and each row read `useCalendar()` only for its
 * "new task / meeting" menu items — so creating ONE task re-rendered every row on the board
 * several times (the editor opening, the events snapshot after the save, the editor closing),
 * and support reported the board "stuck after every action like adding a task or a meeting"
 * (2026-11-04). A row reads this instead.
 */
const CalendarEditorActionsContext = createContext<CalendarEditorActions>({
  showSingleEventEditorModalByType: () => undefined
});

/** A new task / meeting starting at the next round quarter-hour plus one hour, an hour long. */
const initialEventOfType = (eventType: CalendarEventType, customProps?: Partial<CalendarEvent>): CalendarEvent => {
  const start = addHours(roundToNearestMinutes(new Date(), { nearestTo: 15 }), 1);
  const end = addHours(start, 1);

  return {
    end,
    start,
    eventType,
    ...customProps
  } as CalendarEvent;
};

type CalendarProviderProps = {
  children: ReactNode;
};

function CalendarProvider({ children }: CalendarProviderProps) {
  const [calendarSettings, setCalendarSettings] = useState<CalendarSettings>(null);
  const [calendarSettingsId, setCalendarSettingsId] = useState<string>(null);

  const [events, setEvents] = useState<CalendarEvent[]>(null);
  // Office tasks I put in colleagues' calendars — not in `events`, which is MY calendar only and
  // feeds the grid, the contact tabs and "move all overdue to today".
  const [assignedTasks, setAssignedTasks] = useState<CalendarEvent[]>(null);

  const [initialShouldFetchEvents, setInitialShouldFetchEvents] = useState<boolean>(false);
  const [isFetchingEvents, setIsFetchingEvents] = useState<boolean>(true);

  const [currentEvent, setCurrentEvent] = useState<CalendarEvent | null>(null);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);

  const { user } = useAuth();
  const { userSettings } = useFirestore();

  const { enqueueSnackbar } = useSnackbar();
  const { t: translate } = useTranslation();
  const { getMember } = useOrgDirectory();

  const {
    webotCalendarOnEventCreated,
    webotCalendarOnEventUpdated,
    webotCalendarOnEventDeleted,
    getCalendarClientNoteStatus,
    calendarTaskAssign,
    calendarTaskUpdate,
    calendarTaskCancel
  } = useWebotServer();

  // Ask ahead of time whether the event form may offer the "message to invitees" input,
  // so the answer is already cached when the dialog first paints. Waits for a signed-in
  // user (the endpoint needs the token); a failed lookup is retried when a form opens.
  const userEmail: string | undefined = user?.email;
  useEffect(() => {
    if (!userEmail) return;
    warmCalendarClientNoteStatus(getCalendarClientNoteStatus);
    // getCalendarClientNoteStatus is re-created on every provider render; not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  useEffect(() => {
    if (!user || !userSettings || !!calendarSettingsId) {
      return;
    }

    const fetchCalendarSettings = async () => {
      console.log("Fetching calendars owned by user...");

      return await db
        .collection("calendar-settings-v2")
        .where("owners", "array-contains", user.email)
        .onSnapshot(async (snapshot) => {
          console.log("Calendars owned by user:", snapshot.size);

          if (!snapshot.empty) {
            // TODO - handle multiple calendars
            setCalendarSettingsId(snapshot.docs[0].id);
            setCalendarSettings(snapshot.docs[0].data() as CalendarSettings);
          } else {
            console.log("The user has no calendar set up - creating a new calendar");
            const newCalendarSettings: Partial<CalendarSettings> = {
              admin: user.email,
              owners: [user.email],
              calendarName: user.email,
              ownerFullName: userSettings.ownerFullName,
              ownerPhoneNumberInternational: userSettings.ownerPhoneNumber,
              reminderHourBeforeEvent: true,
              reminderOnMorningDayBefore: false,
              reminderOnEveningBeforeEvent: false,
              reminderOnMorningOfEvent: false,
              reminderOnMorningOfTask: true,
              reminderHourBeforeTask: false
            };
            await updateCalendarSettings(newCalendarSettings);
            setCalendarSettingsId(user.email);
            setCalendarSettings(newCalendarSettings as CalendarSettings);
          }
        });
    };

    fetchCalendarSettings();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userSettings, calendarSettingsId]);

  useEffect(() => {
    if (!calendarSettingsId || !initialShouldFetchEvents || !!events) {
      return;
    }

    console.log("In fetch all calendar events effect", calendarSettingsId, events);

    console.log("Fetching all calendar events...");

    const unsubscribeUserEvents = db
      .collection("calendar-settings-v2")
      .doc(calendarSettingsId)
      .collection("calendar-events")
      .withConverter(GenericConverter<CalendarEvent>())
      .onSnapshot((snapshot) => {
        if (snapshot.empty) {
          console.log("Got no events for user:", user.email);
        }

        const events: Array<CalendarEvent> = snapshot.docs
          .filter((doc) => doc.id != null)
          .map((doc) => doc.data())
          .map((event) => ({
            ...event,
            backgroundColor: event.backgroundColor ?? event.textColor
          }));

        console.debug("Events (from context)", events);
        setEvents(events);
        setIsFetchingEvents(false);
      });

    return () => {
      console.log("Cleaning up calendar events listener");
      unsubscribeUserEvents();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarSettingsId, initialShouldFetchEvents]);

  // Office tasks I put in colleagues' calendars ("הקצתי לאחרים"). The read rule is proven from
  // this very `assignedByUserId == me` constraint, and the (assignedByUserId, start) index bounds
  // it to the recent past. A failure (an index still building, a rule not deployed yet) leaves
  // the list empty and everything else working.
  useEffect(() => {
    if (!userEmail || !initialShouldFetchEvents) {
      return;
    }

    const since = subDays(startOfDay(new Date()), ASSIGNED_TASKS_LOOKBACK_DAYS);

    const unsubscribeAssignedTasks = db
      .collectionGroup("calendar-events")
      .where("assignedByUserId", "==", normalizeUserEmail(userEmail))
      .where("start", ">=", since)
      .withConverter(GenericConverter<CalendarEvent>())
      .onSnapshot(
        (snapshot) => {
          setAssignedTasks(
            snapshot.docs
              .map((doc) => doc.data())
              .map((event) => ({ ...event, backgroundColor: event.backgroundColor ?? event.textColor }))
          );
        },
        (error) => {
          console.error("The assigned-tasks listener failed", error);
          setAssignedTasks([]);
        }
      );

    return () => unsubscribeAssignedTasks();
  }, [userEmail, initialShouldFetchEvents]);

  const handleCalendarDialogOpen = (event: CalendarEvent) => {
    setCurrentEvent(event);
    setCalendarDialogOpen(true);
  };

  const handleCalendarDialogClose = () => {
    setCurrentEvent(null);
    setCalendarDialogOpen(false);
  };

  const updateEventTimeById = async (
    eventId: string,
    newStart: Date,
    newEnd: Date,
    newAllDay: boolean
  ) => {
    try {
      const event = events.find((e) => e.id === eventId);

      const updatedEvent: CalendarEvent = {
        ...event,
        start: newStart,
        end: newEnd,
        allDay: newAllDay
      };

      const { id, ...updatedEventWithoutId } = updatedEvent;

      const eventRef = db
        .collection("calendar-settings-v2")
        .doc(calendarSettingsId)
        .collection("calendar-events")
        .doc(eventId);

      await eventRef.set(updatedEventWithoutId, { merge: true });
      console.log("Updated event to", updatedEvent);

      const eventAfterUpdate = (
        await eventRef.withConverter(GenericConverter<CalendarEvent>()).get()
      ).data();

      // Drag/resize is by definition a time change, so this path always notifies.
      _sendEventUpdateToCustomer(eventAfterUpdate, CalendarOperationType.UPDATE, true);

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const officeTaskFailed = (error: unknown): false => {
    console.error("Office task request failed", error);
    enqueueSnackbar(translate(officeTaskErrorKey(error)), { variant: "error" });
    return false;
  };

  /**
   * A task save that crosses calendars — into a colleague's, out of one, or an edit of a task I
   * assigned — goes through the server, the only writer allowed to (firestore.rules). It reports
   * its own outcome; the form shows nothing of its own for these saves.
   */
  const saveOfficeTask = async (
    event: CalendarEvent,
    ownerId: string,
    assigneeId: string
  ): Promise<boolean> => {
    const task = taskFieldsOf(event);
    try {
      if (event.id && ownerId === assigneeId) {
        await calendarTaskUpdate({ ownerUserId: ownerId, taskId: event.id, task });
        enqueueSnackbar(translate("calendarPage.form.taskUpdatedSuccesfully"), { variant: "success" });
        return true;
      }

      const request: REST_schema.CalendarTaskAssignRequest = {
        assigneeUserId: assigneeId,
        task,
        ...(event.id ? { source: { ownerUserId: ownerId, taskId: event.id } } : {})
      };
      const { notification } = await calendarTaskAssign(request);

      const me = normalizeUserEmail(user?.email);
      const name = getMember(assigneeId)?.displayName ?? assigneeId;
      const message =
        assigneeId === me
          ? translate("calendarPage.officeTasks.movedToMe")
          : notification === "sent"
          ? translate("calendarPage.officeTasks.assignedNotified", { name })
          : translate("calendarPage.officeTasks.assigned", { name });
      enqueueSnackbar(message, { variant: "success" });
      return true;
    } catch (error) {
      return officeTaskFailed(error);
    }
  };

  /** One change to a task I assigned (its status, its time) — from a task row. */
  const updateOfficeTask = async (
    event: CalendarEvent,
    patch: Partial<REST_schema.CalendarTaskFields>
  ): Promise<boolean> => {
    try {
      await calendarTaskUpdate({ ownerUserId: event.userId, taskId: event.id, task: patch });
      return true;
    } catch (error) {
      return officeTaskFailed(error);
    }
  };

  const createOrUpdateEvent = async (event: CalendarEvent, options?: CalendarSaveOptions) => {
    const me = normalizeUserEmail(user?.email);
    const ownerId = normalizeUserEmail(options?.ownerUserId) || me;
    const assigneeId = normalizeUserEmail(options?.assigneeUserId) || ownerId;
    if (event.eventType === "task" && (ownerId !== me || assigneeId !== me)) {
      return saveOfficeTask(event, ownerId, assigneeId);
    }

    let { id, ...eventWithoutId } = event;

    const isNewEvent = !id;

    // Compare against the pre-edit event (still in local state — the write below
    // hasn't happened yet) so the server only WhatsApps the client on a real
    // reschedule. Editing colour/title/description must not re-message anyone.
    const previousEvent = id ? events.find((e) => e.id === id) : undefined;
    const timesChanged = isNewEvent || didEventTimesChange(previousEvent, event);

    try {
      const eventRef = db
        .collection("calendar-settings-v2")
        .doc(calendarSettingsId)
        .collection("calendar-events")
        .doc(id); // Note - id will originally be null for new events

      await eventRef.set(eventWithoutId, { merge: true });

      console.log("Updated event", event);

      const eventAfterUpdate = (
        await eventRef.withConverter(GenericConverter<CalendarEvent>()).get()
      ).data();

      const operationType = isNewEvent ? CalendarOperationType.NEW : CalendarOperationType.UPDATE;

      _sendEventUpdateToCustomer(eventAfterUpdate, operationType, timesChanged);

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const deleteEvent = async (eventId: string, ownerUserId?: string) => {
    // A task I assigned lives in the colleague's calendar: only the server may remove it.
    const ownerId = normalizeUserEmail(ownerUserId);
    if (ownerId && ownerId !== normalizeUserEmail(user?.email)) {
      try {
        await calendarTaskCancel({ ownerUserId: ownerId, taskId: eventId });
        enqueueSnackbar(translate("calendarPage.officeTasks.cancelled"), { variant: "success" });
      } catch (error) {
        officeTaskFailed(error);
      }
      return;
    }

    const eventResult = await db
      .collection("calendar-settings-v2")
      .doc(calendarSettingsId)
      .collection("calendar-events")
      .doc(eventId)
      .withConverter(GenericConverter<CalendarEvent>())
      .get();

    await db
      .collection("calendar-settings-v2")
      .doc(calendarSettingsId)
      .collection("calendar-events")
      .doc(eventId)
      .delete();

    _sendEventUpdateToCustomer(eventResult.data(), CalendarOperationType.DELETE);
  };

  const _sendEventUpdateToCustomer = async (
    event: CalendarEvent,
    calendarOperationType: CalendarOperationType,
    timesChanged = true
  ) => {
    if (calendarOperationType === CalendarOperationType.NEW) {
      webotCalendarOnEventCreated(event);
    } else if (calendarOperationType === CalendarOperationType.UPDATE) {
      webotCalendarOnEventUpdated(event, timesChanged);
    } else if (calendarOperationType === CalendarOperationType.DELETE) {
      webotCalendarOnEventDeleted(event);
    }
  };
  const showSingleEventEditorModal = async (eventId: string) => {
    console.log("Show event modal called", eventId);

    const event = events?.find((e) => e.id === eventId);

    if (!event) {
      enqueueSnackbar("Can't open event", { variant: "error" });
      return;
    }

    handleCalendarDialogOpen(event);
  };

  const getInitialEventByType = initialEventOfType;

  // Stable for the provider's lifetime: it touches nothing but two state setters, which
  // React keeps stable. See `CalendarEditorActionsContext`.
  const editorActions = useMemo<CalendarEditorActions>(
    () => ({
      showSingleEventEditorModalByType: (eventType, customProps) => {
        setCurrentEvent(initialEventOfType(eventType, customProps));
        setCalendarDialogOpen(true);
      }
    }),
    []
  );
  const { showSingleEventEditorModalByType } = editorActions;

  const showSingleEventEditorForDateRangeModal = async (start: Date, end: Date) => {
    console.log("Show event modal for range called", start, end);

    let event: Partial<CalendarEvent> = {
      start,
      end
    };

    handleCalendarDialogOpen(event as CalendarEvent);
  };

  const updateCalendarSettings = async (updatedCalendarSettings: Partial<CalendarSettings>) => {
    console.debug("Updating calendar settings to:", updatedCalendarSettings);
    await db
      .collection("calendar-settings-v2")
      .doc(user.email) // to make it easier, the first calendar will have the mail as ID. If we add more calendars, we can auto generate ids
      .set(updatedCalendarSettings, { merge: true }); // could be run in parallel eleswhere, merge to be safe
  };

  return (
    <CalendarContext.Provider
      value={{
        events,
        assignedTasks,
        isFetchingEvents,
        calendarSettings,
        deleteEvent,
        updateOfficeTask,
        initiateLoadEvents: () => setInitialShouldFetchEvents(true),
        createOrUpdateEvent,
        updateEventTimeById,
        getInitialEventByType,
        showSingleEventEditorModal,
        showSingleEventEditorModalByType,
        showSingleEventEditorForDateRangeModal,
        updateCalendarSettings
      }}
    >
      <CalendarEditorActionsContext.Provider value={editorActions}>
        <CalendarEventEditorContext.Provider
          value={{
            open: calendarDialogOpen,
            event: currentEvent,
            onClose: handleCalendarDialogClose
          }}
        >
          {children}
        </CalendarEventEditorContext.Provider>
      </CalendarEditorActionsContext.Provider>
    </CalendarContext.Provider>
  );
}

/**
 * Open the event editor on a new task or meeting — for a component rendered MANY times (a
 * list row). Never re-renders its reader; see `CalendarEditorActionsContext`. Anything that
 * needs the events themselves reads `useCalendar()`.
 */
function useCalendarEditorActions(): CalendarEditorActions {
  return useContext(CalendarEditorActionsContext);
}

/** Renders the global event editor. Mount it once, inside `ListingDialogProvider`. */
function CalendarEventEditorHost(): JSX.Element {
  const { open, event, onClose } = useContext(CalendarEventEditorContext);
  const { deleteEvent, createOrUpdateEvent } = useContext(CalendarContext);

  return (
    <CalendarEventDialog
      open={open}
      event={event}
      onClose={onClose}
      deleteEvent={deleteEvent}
      createOrUpdateEvent={createOrUpdateEvent}
    />
  );
}

export { CalendarProvider, CalendarContext, CalendarEventEditorHost, useCalendarEditorActions };
